/**
 * @hsc/api-client/admin-subscription — typed wrappers for the
 * /v1/admin/tenants/* endpoints owned by the admin_subscription backend
 * module (PR #111, commit e344f32).
 *
 * RBAC: every endpoint here is gated upstream by
 * RequirePlatformAdminForPrefix("/v1/admin/") in cmd/api/main.go. Calling
 * from a non-platform_admin session yields 403; the UI must guard the
 * route accordingly.
 */
import type {
  AdminTenantsListResponse,
  ExtendSubscriptionRequest,
  ExtendSubscriptionResponse,
  ExtensionsListResponse,
} from '@hsc/types';
import { customFetch, type FetchOptions } from './openapi-fetch.js';

type QueryParams = NonNullable<FetchOptions['params']>;

/** GET /v1/admin/tenants — list tenants for the admin selector. */
export async function listAdminTenants(opts: {
  status?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<AdminTenantsListResponse> {
  const params: QueryParams = {};
  if (opts.status !== undefined) params.status = opts.status;
  if (opts.limit !== undefined) params.limit = opts.limit;
  if (opts.offset !== undefined) params.offset = opts.offset;
  return customFetch<AdminTenantsListResponse>({
    url: '/admin/tenants',
    method: 'GET',
    params,
  });
}

/** POST /v1/admin/tenants/{tenant_id}/extend-subscription. */
export async function extendSubscription(
  tenantId: string,
  body: ExtendSubscriptionRequest,
): Promise<ExtendSubscriptionResponse> {
  return customFetch<ExtendSubscriptionResponse>({
    url: `/admin/tenants/${encodeURIComponent(tenantId)}/extend-subscription`,
    method: 'POST',
    data: body,
  });
}

/** GET /v1/admin/tenants/{tenant_id}/subscription-extensions. */
export async function listSubscriptionExtensions(
  tenantId: string,
  limit?: number,
): Promise<ExtensionsListResponse> {
  const params: QueryParams = {};
  if (limit !== undefined) params.limit = limit;
  return customFetch<ExtensionsListResponse>({
    url: `/admin/tenants/${encodeURIComponent(tenantId)}/subscription-extensions`,
    method: 'GET',
    params,
  });
}
